import { api, enviarFormulario } from '../api';

/**
 * As chamadas do cisalhamento direto (onda 265), com os caminhos escritos
 * por extenso — o teste `26` precisa enxergar a string literal.
 */

export interface EnsaioCisalhamentoDireto {
  metodoCodigo: string;
  cabecalho: any | null;
  corpos: any[];
  /** O ponto adotado de cada CP, por base (nominal e corrigida). */
  rupturas: any[];
  /** A envoltória adotada, por base. */
  envoltorias: any[];
  /** Todos os critérios, lado a lado, por CP e base. */
  criterios: any[];
  /** Uma envoltória por critério e base, com «no laudo». */
  envoltoriasCriterios: any[];
  /** A velocidade de cada CP: informada, real e as máximas. */
  velocidades: any[];
  /** As cápsulas da umidade inicial comum a todos os CPs. */
  umidadeComum: any[];
  pacote: { item_codigo: string; cps_por_ensaio: number | null;
            no_escopo: boolean } | null;
  balanca: { id: string; codigo: string; nome: string } | null;
  balancaUmidade: { id: string; codigo: string; nome: string } | null;
  prensa: { id: string; codigo: string; nome: string } | null;
  arquivos: any[];
}

export interface CorpoDeProvaCd {
  cp: any;
  umidade: any[];
  adensamento: any[];
  cisalhamento: any[];
  rupturas: any[];
  criterios: any[];
  velocidade: any | null;
}

export interface TesteDaConferencia {
  teste: number;
  numeroTeste: number | null;
  dataEnsaio: string | null;
  tensaoNormalKpa: number | null;
  adensamento: number;
  cisalhamento: number;
  deslocamentoHorizontalMaxMm: number;
  corpoDeProva: { id: string; ordem: number; identificacao: string;
                  leiturasAtuais: number } | null;
  avisos: string[];
}

export interface ConferenciaCd {
  simulado: boolean;
  arquivo: {
    nome: string; layout: string; formaCp: string | null;
    dimensaoMm: number | null; areaMm2: number | null;
    alturaInicialMm: number | null; deslocamentoMaximoMm: number | null;
  };
  testes: TesteDaConferencia[];
  precisaSubstituir: boolean;
  avisos: string[];
  gravado?: { testes: number; leituras: number; arquivoId: string };
}

export const verCisalhamentoDireto = (id: string) =>
  api<EnsaioCisalhamentoDireto>(`/cisalhamento-direto/${id}`);

export const verCisalhamentoDiretoCp = (cpId: string) =>
  api<CorpoDeProvaCd>(`/cisalhamento-direto/cp/${cpId}`);

export const gravarCisalhamentoDiretoFicha = (id: string, corpo: unknown) =>
  api<any>(`/cisalhamento-direto/${id}/ficha`, { metodo: 'POST', corpo });

export const criarCisalhamentoDiretoCp = (id: string, corpo: unknown) =>
  api<any>(`/cisalhamento-direto/${id}/corpos`, { metodo: 'POST', corpo });

export const editarCisalhamentoDiretoCp = (cpId: string, corpo: unknown) =>
  api<any>(`/cisalhamento-direto/cp/${cpId}`, { metodo: 'PATCH', corpo });

export const gravarCisalhamentoDiretoUmidade = (id: string, corpo: unknown) =>
  api<any[]>(`/cisalhamento-direto/${id}/umidade`, { metodo: 'POST', corpo });

export const descartarCisalhamentoDiretoCapsula = (uid: string, motivo: string) =>
  api<any>(`/cisalhamento-direto/umidade/${uid}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

export const colarLeiturasCisalhamentoDireto = (cpId: string, corpo: unknown) =>
  api<{ gravadas: number; substituidas: number }>(
    `/cisalhamento-direto/cp/${cpId}/leituras`, { metodo: 'POST', corpo });

export const descartarLeiturasCisalhamentoDireto = (cpId: string, corpo: unknown) =>
  api<{ descartadas: number }>(
    `/cisalhamento-direto/cp/${cpId}/leituras/descartar`, { metodo: 'POST', corpo });

export const tracarCisalhamentoDireto = (cpId: string, corpo: unknown) =>
  api<any>(`/cisalhamento-direto/cp/${cpId}/tracado`, { metodo: 'POST', corpo });

export const gravarCriteriosNoLaudoCd = (
  id: string, criterios: string[], baseNoLaudo?: 'nominal' | 'ambas',
) => api<any[]>(`/cisalhamento-direto/${id}/criterios-no-laudo`,
  { metodo: 'POST', corpo: { criterios, baseNoLaudo } });

/**
 * Importa a planilha do relatório (modelo Owntec da MS 101) para o
 * ensaio inteiro: cada «Teste n» vai ao CP de ordem n. `simular` devolve
 * a conferência sem gravar nada.
 */
export function importarPlanilhaCisalhamentoDireto(
  id: string,
  arquivo: File,
  opcoes: { simular?: boolean; substituir?: boolean;
            mapa?: Record<string, string> } = {},
  aoProgredir?: (f: number) => void,
): Promise<ConferenciaCd> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  if (opcoes.simular) form.append('simular', 'true');
  if (opcoes.substituir) form.append('substituir', 'true');
  if (opcoes.mapa && Object.keys(opcoes.mapa).length) {
    form.append('mapa', JSON.stringify(opcoes.mapa));
  }
  return enviarFormulario<ConferenciaCd>(
    `/cisalhamento-direto/${id}/importar`, form, aoProgredir);
}
